// The SPA's end of `BroadcastChannelTopic.PostChannel` — how a finished mailbox connect reaches
// the screen that asked for it.
//
// The OAuth providers send the user back to `/chrome-extension/post?type=…` in a tab of its own;
// that page announces the result here, and the "Please connect your work email" side view that
// started the connect hears it and moves on to the composer. Exchange connects without leaving
// the page and announces the same way. The transport is `BroadcastChannelService`, which picks the
// extension route or a native same-origin channel per send (see `authBroadcast.ts`).
import { BroadcastChannelService, BroadcastChannelTopic, type BroadcastMessage } from '@woo-ext/protocol'

/** `BroadcastChannelEventType` — the values the provider callbacks put in `?type`. */
export const PostEventType = {
  GmailConnected: 'gmailConnected',
  GmailConnectError: 'gmailConnectError',
  OutlookConnected: 'outlookConnected',
  OutlookConnectError: 'outlookConnectError',
  ExchangeConnected: 'exchangeConnected',
} as const

const CONNECTED: ReadonlySet<string> = new Set([
  PostEventType.GmailConnected,
  PostEventType.OutlookConnected,
  PostEventType.ExchangeConnected,
])

/** Whether a post-channel message reports a mailbox that is now connected. */
export function isMailboxConnected(message: BroadcastMessage): boolean {
  return CONNECTED.has(message.type)
}

const service = new BroadcastChannelService()

/**
 * `post.page.component`'s `$onInit`: `create(PostChannel).postMessage({ type })`, whatever the
 * type — an error is announced too, and the listeners ignore what they do not handle.
 */
export function publishPostEvent(type: string | undefined): void {
  if (type === undefined) return
  const channel = service.createAndSubscribe(BroadcastChannelTopic.PostChannel, () => undefined)
  channel.postMessage({ type })
  channel.destroy()
}

/**
 * `setupOnEmailSignin` — `createAndSubscribe(PostChannel, …, false)`.
 *
 * ⚠️ `ignoreSelfMessages: false`, as upstream passes it. The Exchange dialog announces from the
 * SAME page as the side view that listens, and that announcement has to be heard.
 */
export function subscribePostEvents(handler: (message: BroadcastMessage) => void): () => void {
  const channel = service.createAndSubscribe(BroadcastChannelTopic.PostChannel, handler, {
    ignoreSelfMessages: false,
  })
  return () => {
    channel.destroy()
  }
}
